import { useCallback, useRef, useState } from "react";
import { UploadCloud, X, Image as ImageIcon, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Reusable drag-and-drop file uploader (images by default, PDFs via `accept` prop).
 *
 * Props:
 *   value:      current file URL (may be full URL, absolute path, or empty)
 *   onChange:   called with the new URL when a file is uploaded / cleared
 *   label:      shown above the dropzone
 *   accept:     MIME/type filter. Defaults to "image/*". Pass "application/pdf" for PDFs.
 */
const ImageUpload = ({ value, onChange, label = "Cover image", accept = "image/*" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const isRelative = value && value.startsWith("/api/");
  const displayUrl = isRelative ? `${BACKEND_URL}${value}` : value;
  const isPdf = accept.includes("pdf");

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const validImage = accept.includes("image") && file.type.startsWith("image/");
    const validPdf = accept.includes("pdf") && (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));
    if (!validImage && !validPdf) {
      toast.error(isPdf ? "Please choose a PDF file" : "Please choose an image (JPG, PNG, WebP)");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB). Try compressing first.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/admin/upload", fd);
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [onChange, accept, isPdf]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div className="space-y-2" data-testid="image-upload">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          {value && (
            <button type="button" onClick={clear} className="text-xs text-destructive inline-flex items-center gap-1" data-testid="image-upload-clear">
              <X size={12} /> Remove
            </button>
          )}
        </div>
      )}

      {displayUrl ? (
        <div
          className="relative rounded-lg overflow-hidden border border-border bg-[hsl(var(--secondary))]/40 group cursor-pointer"
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          data-testid="image-upload-preview"
        >
          {isPdf ? (
            <div className="p-6 h-48 flex flex-col items-center justify-center gap-2 text-[hsl(var(--foreground))]/80">
              <FileText size={40} strokeWidth={1.25} className="text-[hsl(var(--primary))]" />
              <div className="text-sm font-medium truncate max-w-full">{value.split("/").pop()}</div>
              <a href={displayUrl} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]" onClick={(e) => e.stopPropagation()}>Open PDF ↗</a>
            </div>
          ) : (
            <img src={displayUrl} alt="Preview" className="w-full h-48 object-cover" />
          )}
          <div className={`absolute inset-0 flex items-center justify-center bg-black/50 text-white text-xs uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 transition ${uploading ? "opacity-100" : ""}`}>
            {uploading ? (<><Loader2 size={16} className="animate-spin mr-2" /> Uploading…</>) : "Click or drop to replace"}
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`relative rounded-lg border-2 border-dashed p-8 cursor-pointer transition text-center ${
            dragOver ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border hover:border-[hsl(var(--primary))]/40 bg-[hsl(var(--secondary))]/30"
          }`}
          data-testid="image-upload-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 size={24} className="animate-spin text-[hsl(var(--primary))]" />
              <span className="text-sm">Uploading…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <UploadCloud size={30} strokeWidth={1.25} className="text-[hsl(var(--primary))]" />
              <span className="text-sm font-medium text-[hsl(var(--foreground))]">Drag & drop {isPdf ? "a PDF" : "an image"} here</span>
              <span className="text-xs">or click to choose · {isPdf ? "PDF" : "JPG / PNG / WebP"} · max 15 MB</span>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="image-upload-input"
      />

      {value && (
        <div className="text-[10px] text-muted-foreground break-all">
          URL: <code>{value}</code>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
